import React, { useRef, useEffect, useMemo } from 'react';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { MVTLayer } from '@deck.gl/geo-layers';
import { ScatterplotLayer, PolygonLayer, TextLayer } from '@deck.gl/layers';
import { API_ENDPOINTS } from '../../config/chargerApiConfig';
import { getCircuitColorHex, parseLadwpCapacityValue } from '../charger_planning/circuitColorUtils';
import { extractPolygonCoordinateSets } from '../../utils/geoJsonUtils';
import { logger } from '../../utils/logger';
import { outlookIndices, currentYearIndex, getCurrentYear, circuitABankDisplay, circuitAlcDisplay, formatOutlookEntriesLine } from '../../utils/capacityOutlook';

const hexToRgb = (hex, alpha = 255) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16),
        alpha,
      ]
    : [0, 0, 0, alpha];
};

const parseColorValue = (value, alpha = 255) => {
  if (value == null) return null;
  if (Array.isArray(value) && value.length >= 3) {
    const [r, g, b, a] = value;
    const n = (v) =>
      Number.isFinite(v)
        ? Math.round(Math.min(255, Math.max(0, v <= 1 && v >= 0 ? v * 255 : v)))
        : 0;
    return [n(r), n(g), n(b), a !== undefined ? n(a) : alpha];
  }
  if (typeof value === 'string' && value.trim().startsWith('#')) {
    const hex = value.trim().slice(1);
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return [r, g, b, alpha];
  }
  return null;
};

const getColorFromContourProperties = (
  properties = {},
  keys = [],
  fallback = [79, 70, 229, 100]
) => {
  for (const key of keys) {
    const parsed = parseColorValue(properties?.[key]);
    if (parsed) return parsed;
  }
  return fallback;
};

const TRAFFIC_COLORS = [
  '#006400',
  '#00FF00',
  '#FFFF00',
  '#FFA500',
  '#FF8C00',
  '#FF4500',
  '#FF0000',
];
const TRAFFIC_THRESHOLDS = [1000, 2000, 3000, 4000, 5000, 6000, Infinity];
const getTrafficColor = (volume) => {
  const idx = TRAFFIC_THRESHOLDS.findIndex((t) => volume <= t);
  return TRAFFIC_COLORS[idx];
};

const RAMP_COLORS = {
  OR: '#2563eb',
  FR: '#9333ea',
};

const getTrafficPointColor = (traffic) => {
  const stationType = (traffic.station_type || 'ML').toString().trim().toUpperCase();
  if (stationType === 'OR' || stationType === 'FR') {
    return RAMP_COLORS[stationType] || '#64748b';
  }
  const truckVolume = Number(traffic.truck_volume ?? traffic.volume ?? 0);
  return getTrafficColor(truckVolume);
};

const UTILITY_COLORS = [
  '#EDF67D',
  '#F896D8',
  '#CA7DF9',
  '#724CF9',
  '#564592',
  '#CC3F0C',
];

const COUNTY_COLORS = [
  '#2563EB',
  '#7C3AED',
  '#0D9488',
  '#DC2626',
  '#D97706',
  '#16A34A',
  '#0891B2',
  '#BE185D',
  '#4F46E5',
  '#CA8A04',
  '#059669',
  '#9333EA',
];

const getCountyColorHex = (county) => {
  const rawId = county?.GEOID ?? county?.id ?? '';
  const idString = String(rawId);
  const hash = idString
    .split('')
    .reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return COUNTY_COLORS[hash % COUNTY_COLORS.length];
};

const getUtilityTerritoryName = (utility = {}) => {
  const props = utility?.properties || {};
  return (
    utility?.Utility ||
    utility?.utility ||
    utility?.name ||
    props.Utility ||
    props.utility ||
    props.NAME ||
    props.name ||
    'Unknown Utility'
  );
};

const moveLayerGroupToTop = (layerGroups, interactiveTopLayerId) => {
  if (!interactiveTopLayerId) {
    return layerGroups.flatMap((group) => group.layers);
  }
  const prioritizedGroups = layerGroups.filter((group) => group.id === interactiveTopLayerId);
  if (prioritizedGroups.length === 0) {
    return layerGroups.flatMap((group) => group.layers);
  }
  return [
    ...layerGroups.filter((group) => group.id !== interactiveTopLayerId).flatMap((group) => group.layers),
    ...prioritizedGroups.flatMap((group) => group.layers),
  ];
};

const getPolygonCenter = (coordinates) => {
  if (!Array.isArray(coordinates) || coordinates.length === 0) return null;
  const ring = coordinates[0];
  if (!Array.isArray(ring) || ring.length === 0) return null;
  let sumLng = 0;
  let sumLat = 0;
  let count = 0;
  ring.forEach((point) => {
    if (!Array.isArray(point) || point.length < 2) return;
    const lng = Number(point[0]);
    const lat = Number(point[1]);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
    sumLng += lng;
    sumLat += lat;
    count += 1;
  });
  if (count === 0) return null;
  return [sumLng / count, sumLat / count];
};

const getCircuitProp = (props, ...keys) => {
  if (!props) return null;
  for (const key of keys) {
    const value = props[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
};

const formatMwNumber = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  const text = num.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
  return text || '0';
};

const formatKwTextToMw = (value) => {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const nums = raw.match(/(\d+(?:,\d+)?(?:\.\d+)?)/g);
  const parsed = (nums || [])
    .map((x) => Number(String(x).replace(/,/g, '')))
    .filter((x) => Number.isFinite(x));
  if (parsed.length === 0) return null;

  if (raw.includes('>')) {
    const low = formatMwNumber(parsed[0] / 1000);
    return low ? `${low}+ MW` : null;
  }

  if (raw.includes('-') && parsed.length >= 2) {
    const start = formatMwNumber(parsed[0] / 1000);
    const end = formatMwNumber(parsed[1] / 1000);
    return start && end ? `${start}-${end} MW` : null;
  }

  const single = formatMwNumber(parsed[0] / 1000);
  return single ? `${single} MW` : null;
};

const formatHostingCapacityMw = (props) => {
  const utility = getCircuitProp(props, 'utility', 'Utility');
  const range = getCircuitProp(props, 'capacity_range_kw', 'capacity_kw_range', 'capacity_range');
  const numeric = getCircuitProp(props, 'capacity', 'capacity_kw', 'hosting_capacity');

  if (String(utility || '').toUpperCase() === 'LADWP' && range != null) {
    const mw = formatKwTextToMw(range);
    if (mw) return mw;
  }

  if (numeric != null) {
    const n = Number(numeric);
    if (Number.isFinite(n)) {
      const mw = formatMwNumber(n / 1000);
      return mw ? `${mw} MW` : null;
    }
    const mw = formatKwTextToMw(numeric);
    if (mw) return mw;
  }

  if (range != null) {
    const mw = formatKwTextToMw(range);
    if (mw) return mw;
  }

  return null;
};

const normalizeCircuitSelectionValue = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

const normalizeCircuitUtilityValue = (value) => {
  const raw = String(value ?? '').trim().toLowerCase();
  if (!raw) return '';
  if (raw === 'sce' || raw.includes('southern california edison')) return 'sce';
  if (raw.includes('ladwp') || (raw.includes('los angeles') && raw.includes('water') && raw.includes('power'))) {
    return 'ladwp';
  }
  return normalizeCircuitSelectionValue(raw);
};

const buildCircuitSelection = ({ utility = null, circuitName = null, circuitId = null } = {}) => {
  const normalizedName = normalizeCircuitSelectionValue(circuitName);
  const normalizedId = normalizeCircuitSelectionValue(circuitId);
  if (!normalizedName && !normalizedId) return null;
  return { utility, circuitName, circuitId };
};

const getCircuitSelectionFromProps = (props = {}) =>
  buildCircuitSelection({
    utility: getCircuitProp(props, 'utility', 'Utility'),
    circuitName: getCircuitProp(props, 'circuit_name', 'Circuit_Name', 'name'),
    circuitId: getCircuitProp(props, 'circuit_id', 'Circuit_ID', 'id'),
  });

const circuitsMatch = (left, right) => {
  if (!left || !right) return false;
  const leftUtility = normalizeCircuitUtilityValue(left.utility);
  const rightUtility = normalizeCircuitUtilityValue(right.utility);
  if (leftUtility && rightUtility && leftUtility !== rightUtility) return false;

  const leftId = normalizeCircuitSelectionValue(left.circuitId);
  const rightId = normalizeCircuitSelectionValue(right.circuitId);
  if (leftId && rightId) return leftId === rightId;

  const leftName = normalizeCircuitSelectionValue(left.circuitName);
  const rightName = normalizeCircuitSelectionValue(right.circuitName);
  if (leftName && rightName) return leftName === rightName;

  return false;
};

const isSelectedCircuitFeature = (feature, selectedCircuit) =>
  circuitsMatch(selectedCircuit, getCircuitSelectionFromProps(feature?.properties || {}));

const getBaseCircuitLineColor = (feature) => {
  const utility = feature.properties?.utility || 'UNKNOWN';
  if (utility === 'SCE') {
    const cap =
      feature.properties?.capacity ??
      feature.properties?.capacity_kw ??
      feature.properties?.hosting_capacity ??
      0;
    const hex = getCircuitColorHex(cap);
    if (hex) {
      const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      if (m) {
        return [
          parseInt(m[1], 16),
          parseInt(m[2], 16),
          parseInt(m[3], 16),
          255,
        ];
      }
    }
  } else if (utility === 'LADWP') {
    const capacityValue = parseLadwpCapacityValue(feature.properties ?? {});
    const hex = getCircuitColorHex(capacityValue);
    if (hex) {
      const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      if (m) {
        return [
          parseInt(m[1], 16),
          parseInt(m[2], 16),
          parseInt(m[3], 16),
          255,
        ];
      }
    }
  }
  return [153, 153, 153, 255];
};

const formatSubstationMw = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return `${num.toFixed(2).replace(/\.?0+$/, '')} MW`;
};

// Site label = alphabetic name before the voltage ratio, e.g.
// 'Alhambra 66/16 (D)' -> 'Alhambra', 'Aha P.T. 33/12 (A)' -> 'Aha P.T.'.
const substationSiteLabel = (name = '') => {
  const trimmed = String(name).split(/\s+\d/)[0].trim();
  return trimmed || String(name) || 'N/A';
};

// Split a substation/bank name into the site name and the bank (voltage) part,
// e.g. 'Highland 66/12 kV' -> { name: 'Highland', bank: '66/12 kV' }.
const splitSubstationNameBank = (full) => {
  const s = String(full ?? '').trim();
  if (!s) return { name: 'N/A', bank: 'N/A' };
  const idx = s.search(/\d/);
  if (idx <= 0) return { name: s, bank: 'N/A' };
  return { name: s.slice(0, idx).trim() || s, bank: s.slice(idx).trim() };
};

// Per-bank outlook string anchored on the current calendar year (current year
// first, then the following years that exist in the series).
const substationBankOutlook = (bank = {}) => {
  const outlook = bank.capacity_multiyear || {};
  const planYear = Number(outlook.plan_year);
  const series = Array.isArray(outlook.estimated_available) ? outlook.estimated_available : [];
  const parts = [];
  outlookIndices(planYear, series.length).forEach((i) => {
    const mw = formatSubstationMw(series[i]);
    if (mw && Number.isFinite(planYear)) parts.push(`${planYear + i}: ${mw}`);
  });
  return parts;
};

const buildSingleSubstationTooltip = (sub = {}) => {
  const name = sub.substation_name ?? 'N/A';
  const system = sub.system_name || 'N/A';
  const outlook = sub.capacity_multiyear || {};
  const planYear = Number(outlook.plan_year);
  const series = Array.isArray(outlook.estimated_available) ? outlook.estimated_available : [];

  // Headline = current-year estimated available capacity from the series; fall
  // back to the stored headline (plan_year + 1) when the series has no value.
  const curIdx = currentYearIndex(planYear, series.length);
  const currentYearMw =
    curIdx != null ? formatSubstationMw(series[curIdx]) : null;
  const headlineMw = currentYearMw ?? formatSubstationMw(sub.capacity_mw) ?? 'N/A';
  const headlineYear = Number.isFinite(planYear) ? planYear + (curIdx ?? 0) : getCurrentYear();

  const lines = [
    `Substation: ${name}`,
    `System: ${system}`,
    `Est. Available Capacity (${headlineYear}): ${headlineMw}`,
  ];
  const outlookParts = substationBankOutlook(sub);
  if (outlookParts.length > 0) {
    lines.push(`Outlook: ${outlookParts.join('  |  ')}`);
  }
  return lines.join('\n');
};

const buildSubstationTooltipText = (sub = {}) => {
  const banks = Array.isArray(sub.banks) ? sub.banks : [sub];
  if (banks.length <= 1) {
    return buildSingleSubstationTooltip(banks[0] || sub);
  }
  // Multiple transformer banks share this location. Capacities are per voltage
  // bus and are NOT summed (different voltages can't carry a single feeder).
  const site = substationSiteLabel(banks[0]?.substation_name);
  const systems = [...new Set(banks.map((b) => b.system_name).filter(Boolean))];
  const lines = [
    `Substation site: ${site}`,
    `${banks.length} transformer banks at this location:`,
  ];
  banks.forEach((b) => {
    lines.push(`  • ${b.substation_name ?? 'N/A'}`);
    const parts = substationBankOutlook(b);
    if (parts.length > 0) {
      lines.push(`      Est. Available: ${parts.join('  |  ')}`);
    } else {
      const cap = formatSubstationMw(b.capacity_mw) ?? 'N/A';
      lines.push(`      Est. Available: ${cap}`);
    }
  });
  lines.push(`System: ${systems.length ? systems.join(', ') : 'N/A'}`);
  return lines.join('\n');
};

const buildCircuitTooltipText = (props = {}) => {
  const utility = getCircuitProp(props, 'utility', 'Utility') ?? 'N/A';
  const circuitName = getCircuitProp(props, 'circuit_name', 'Circuit_Name', 'name') ?? 'N/A';
  const circuitId = getCircuitProp(props, 'circuit_id', 'Circuit_ID', 'id') ?? 'N/A';
  const voltage = getCircuitProp(props, 'circuit_voltage', 'voltage', 'voltage_kv', 'kv') ?? 'N/A';
  const hostingCapacity = formatHostingCapacityMw(props) ?? 'N/A';

  // LADWP has no substation attribution (substation is null) -> renders as N/A,
  // keeping the layout aligned with SCE.
  const { name: substationName, bank: substationBank } = splitSubstationNameBank(
    getCircuitProp(props, 'substation_name', 'substation')
  );

  const rawAlc = getCircuitProp(props, 'alc_multiyear');
  const circuitAlc = circuitAlcDisplay(rawAlc);
  const aBank = circuitABankDisplay(rawAlc);
  const lines = [
    `Utility: ${utility}`,
    `Circuit: ${circuitName}`,
    `Circuit ID: ${circuitId}`,
    `Voltage (kV): ${voltage}`,
    `Hosting Capacity: ${hostingCapacity}`,
    `Substation: ${substationName}`,
    `Bank: ${substationBank}`,
  ];

  if (circuitAlc.outlookEntries.length > 0) {
    lines.push(`Est. Capacity Outlook: ${formatOutlookEntriesLine(circuitAlc.outlookEntries)}`);
  }

  lines.push(`A Bank: ${aBank.name ?? 'N/A'}`);
  lines.push(`A Bank Capacity: ${aBank.currentMw ?? 'N/A'}`);
  if (aBank.outlookEntries.length > 0) {
    lines.push(`A Bank Outlook: ${formatOutlookEntriesLine(aBank.outlookEntries)}`);
  }

  return lines.join('\n');
};

/**
 * Overlay for Regulator map: MVT layers (circuits, heatmap, contours) plus GeoJSON/array
 * layers (existing chargers, warehouses, intermodal, service centers, traffic, utilities,
 * SCAQMD, overburdened communities).
 */
export const RegulatorChargerLayersOverlay = ({
  map,
  parcelsVisible = false,
  circuitsVisible = false,
  substationsVisible = false,
  heatmapVisible = false,
  contoursVisible = false,
  currentZoom = 10,
  existingChargersVisible = false,
  warehousesVisible = false,
  intermodalTerminalsVisible = false,
  serviceCentersVisible = false,
  trafficVolumeVisible = false,
  utilitiesVisible = false,
  sraVisible = false,
  countiesVisible = false,
  overburdenedVisible = false,
  calEnviroScreenVisible = false,
  onCircuitClick,
  onSubstationClick,
  onChargingStationClick,
  onWarehouseClick,
  onTruckStopClick,
  onSraClick,
  mhdInfrastructure = [],
  substations = [],
  warehouses = [],
  truckStops = [],
  trafficData = [],
  utilitiesData = [],
  sraData = [],
  countiesData = [],
  disadvantagedCommunities = [],
  calEnviroScreen = [],
  selectedCircuit = null,
  selectedSubstation = null,
  interactiveTopLayerId = null,
}) => {
  const overlayRef = useRef(null);
  const getTooltip = useMemo(
    () => (info) => {
      const object = info?.object;
      const layerId = info?.layer?.id;
      if (!object) return null;
      if (layerId === 'regulator-circuits-mvt') {
        return {
          text: buildCircuitTooltipText(object?.properties || {}),
        };
      }
      if (layerId === 'regulator-substations-layer') {
        return {
          text: buildSubstationTooltipText(object || {}),
        };
      }
      if (layerId === 'regulator-sra-layer' || layerId === 'regulator-sra-text-layer') {
        const monitoring = object?.properties?.monitoring ?? object?.monitoring;
        return {
          text: `SRA: ${monitoring ?? 'N/A'}`,
        };
      }
      if (layerId === 'regulator-utilities-layer') {
        const utilityName = getUtilityTerritoryName(object);
        const acronym = object?.Acronym || object?.properties?.Acronym;
        return {
          text: acronym ? `Utility: ${utilityName} (${acronym})` : `Utility: ${utilityName}`,
        };
      }
      if (layerId !== 'regulator-counties-layer') return null;
      const props = object.properties || {};
      const countyName =
        object.NAMELSAD ||
        object.NAME ||
        props.NAMELSAD ||
        props.NAME ||
        'Unknown County';
      const countyGeoid = object.GEOID || props.GEOID;
      const geoid = countyGeoid ? ` (${countyGeoid})` : '';
      return {
        text: `County: ${countyName}${geoid}`,
      };
    },
    []
  );

  const parcelLayers = useMemo(() => {
    if (!parcelsVisible || currentZoom < 14) return [];
    return [
      new MVTLayer({
        id: 'regulator-parcels-mvt',
        data: API_ENDPOINTS.vectorTiles.parcels,
        minZoom: 0,
        maxZoom: 22,
        pickable: true,
        filled: true,
        stroked: true,
        getFillColor: () => [79, 70, 229, 40],
        getLineColor: () => [79, 70, 229, 180],
        getLineWidth: 1,
        lineWidthMinPixels: 1,
      }),
    ];
  }, [parcelsVisible, currentZoom]);

  const circuitLayers = useMemo(() => {
    if (!circuitsVisible) return [];
    const normalizedZoom = Math.min(22, Math.max(0, currentZoom));
    return [
      new MVTLayer({
        id: 'regulator-circuits-mvt',
        data: API_ENDPOINTS.vectorTiles.circuitsCombined,
        minZoom: 0,
        maxZoom: 22,
        pickable: true,
        autoHighlight: true,
        highlightColor: [255, 255, 255, 100],
        lineWidthMinPixels: normalizedZoom <= 14 ? 2 : 3,
        getLineColor: getBaseCircuitLineColor,
        getLineWidth: normalizedZoom <= 14 ? 2 : 3,
        onClick: (info) => {
          if (info?.object && onCircuitClick) onCircuitClick(info);
        },
        updateTriggers: {
          getLineColor: [normalizedZoom],
          getLineWidth: [normalizedZoom],
        },
      }),
    ];
  }, [circuitsVisible, currentZoom, onCircuitClick]);

  const selectedCircuitLayers = useMemo(() => {
    if (!circuitsVisible || !selectedCircuit) return [];
    const normalizedZoom = Math.min(22, Math.max(0, currentZoom));
    const glowWidth = normalizedZoom <= 14 ? 8 : 10;
    const coreWidth = normalizedZoom <= 14 ? 4 : 5;
    const selectionTrigger = [
      normalizeCircuitSelectionValue(selectedCircuit.utility),
      normalizeCircuitSelectionValue(selectedCircuit.circuitId),
      normalizeCircuitSelectionValue(selectedCircuit.circuitName),
      normalizedZoom,
    ];

    return [
      new MVTLayer({
        id: 'regulator-circuits-selected-glow',
        data: API_ENDPOINTS.vectorTiles.circuitsCombined,
        minZoom: 0,
        maxZoom: 22,
        pickable: false,
        lineWidthUnits: 'pixels',
        parameters: { depthTest: false },
        getLineColor: (feature) =>
          isSelectedCircuitFeature(feature, selectedCircuit) ? [96, 165, 250, 140] : [0, 0, 0, 0],
        getLineWidth: (feature) => (isSelectedCircuitFeature(feature, selectedCircuit) ? glowWidth : 0),
        updateTriggers: {
          getLineColor: selectionTrigger,
          getLineWidth: selectionTrigger,
        },
      }),
      new MVTLayer({
        id: 'regulator-circuits-selected-core',
        data: API_ENDPOINTS.vectorTiles.circuitsCombined,
        minZoom: 0,
        maxZoom: 22,
        pickable: false,
        lineWidthUnits: 'pixels',
        parameters: { depthTest: false },
        getLineColor: (feature) =>
          isSelectedCircuitFeature(feature, selectedCircuit) ? [37, 99, 235, 255] : [0, 0, 0, 0],
        getLineWidth: (feature) => (isSelectedCircuitFeature(feature, selectedCircuit) ? coreWidth : 0),
        updateTriggers: {
          getLineColor: selectionTrigger,
          getLineWidth: selectionTrigger,
        },
      }),
    ];
  }, [circuitsVisible, currentZoom, selectedCircuit]);

  const heatmapLayers = useMemo(() => {
    if (!heatmapVisible) return [];
    const getHeatmapColor = (weight) => {
      if (weight > 1000) return [128, 0, 38, 180];
      if (weight > 500) return [189, 0, 38, 180];
      if (weight > 200) return [227, 26, 28, 180];
      if (weight > 100) return [252, 78, 42, 180];
      if (weight > 50) return [253, 141, 60, 180];
      if (weight > 20) return [254, 178, 76, 180];
      if (weight > 10) return [254, 217, 118, 180];
      return [255, 237, 160, 180];
    };
    return [
      new MVTLayer({
        id: 'regulator-heatmap-mvt',
        data: API_ENDPOINTS.vectorTiles.heatmapHexagons,
        minZoom: 0,
        maxZoom: 22,
        pickable: true,
        filled: true,
        stroked: false,
        getFillColor: (f) => getHeatmapColor(f.properties?.weight ?? 0),
      }),
    ];
  }, [heatmapVisible]);

  const contourLayers = useMemo(() => {
    if (!contoursVisible || currentZoom >= 13) return [];
    return [
      new MVTLayer({
        id: 'regulator-contours-mvt',
        data: API_ENDPOINTS.vectorTiles.circuitContours,
        minZoom: 0,
        maxZoom: 12,
        // Let circuit clicks pass through to the underlying line layer.
        pickable: false,
        filled: true,
        stroked: false,
        getFillColor: (f) => {
          const c = getColorFromContourProperties(
            f.properties,
            ['fill_color', 'fillColor', 'color', 'fill'],
            [79, 70, 229, 180]
          );
          return [c[0], c[1], c[2], 180];
        },
      }),
    ];
  }, [contoursVisible, currentZoom]);

  const mhdLayers = useMemo(() => {
    if (
      !existingChargersVisible ||
      !Array.isArray(mhdInfrastructure) ||
      mhdInfrastructure.length === 0
    )
      return [];
    const processed = mhdInfrastructure
      .map((item) => {
        const lat = parseFloat(item.lat);
        const lon = parseFloat(item.lon);
        if (Number.isNaN(lat) || Number.isNaN(lon)) return null;
        return { ...item, position: [lon, lat] };
      })
      .filter(Boolean);
    if (processed.length === 0) return [];
    return [
      new ScatterplotLayer({
        id: 'regulator-mhd-layer',
        data: processed,
        pickable: true,
        opacity: 0.8,
        stroked: true,
        filled: true,
        radiusScale: 1,
        radiusMinPixels: 8,
        radiusMaxPixels: 12,
        lineWidthMinPixels: 2,
        getPosition: (d) => d.position,
        getRadius: 12,
        getFillColor: [5, 150, 105],
        getLineColor: [6, 95, 70],
        onClick: (info) => {
          if (info?.object && onChargingStationClick) {
            onChargingStationClick(info.object, info);
          }
        },
      }),
    ];
  }, [existingChargersVisible, mhdInfrastructure, onChargingStationClick]);

  const substationLayers = useMemo(() => {
    if (!substationsVisible || !Array.isArray(substations) || substations.length === 0) {
      return [];
    }
    // Group banks that share a location (multi-bank substations overlap exactly),
    // so a single point renders per site and the tooltip lists every bank.
    const groups = new Map();
    substations.forEach((item) => {
      const lat = parseFloat(item.latitude);
      const lon = parseFloat(item.longitude);
      if (Number.isNaN(lat) || Number.isNaN(lon)) return;
      const key = `${lon.toFixed(6)},${lat.toFixed(6)}`;
      const existing = groups.get(key);
      if (existing) {
        existing.banks.push(item);
      } else {
        groups.set(key, { position: [lon, lat], banks: [item] });
      }
    });
    const processed = [...groups.values()].map((group) => ({
      ...group.banks[0],
      position: group.position,
      banks: group.banks,
    }));
    if (processed.length === 0) return [];
    // Shrink the markers as the map zooms out so dense regions don't turn into
    // an overlapping cluster of amber dots. t ramps 0 -> 1 between zoom 7 and 13.
    const zoom = Math.min(22, Math.max(0, currentZoom));
    const t = Math.min(1, Math.max(0, (zoom - 7) / (13 - 7)));
    const radiusPx = 3.5 + t * 6.5;
    const radiusMinPx = 2.5 + t * 4.5;
    const radiusMaxPx = 8 + t * 6;
    const lineWidthPx = 1 + t;
    return [
      new ScatterplotLayer({
        id: 'regulator-substations-layer',
        data: processed,
        pickable: true,
        opacity: 0.9,
        stroked: true,
        filled: true,
        radiusUnits: 'pixels',
        radiusMinPixels: radiusMinPx,
        radiusMaxPixels: radiusMaxPx,
        lineWidthUnits: 'pixels',
        lineWidthMinPixels: lineWidthPx,
        getPosition: (d) => d.position,
        getRadius: radiusPx,
        // Amber node with a dark ring; distinct from chargers (green),
        // intermodal (purple), service centers (teal), traffic (varied).
        getFillColor: [234, 179, 8, 235],
        getLineColor: [120, 53, 15, 255],
        updateTriggers: {
          getRadius: [radiusPx],
        },
        onClick: (info) => {
          if (info?.object && onSubstationClick) {
            onSubstationClick(info.object, info.coordinate);
          }
        },
      }),
    ];
  }, [substationsVisible, substations, currentZoom, onSubstationClick]);

  const selectedSubstationLayers = useMemo(() => {
    if (!substationsVisible || !selectedSubstation || !Array.isArray(selectedSubstation.position)) {
      return [];
    }
    const [lon, lat] = selectedSubstation.position;
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return [];
    const selectionTrigger = `${lon.toFixed(6)},${lat.toFixed(6)}`;
    // Keep the highlight rings proportional to the (now zoom-scaled) marker.
    const zoom = Math.min(22, Math.max(0, currentZoom));
    const t = Math.min(1, Math.max(0, (zoom - 7) / (13 - 7)));
    const glowRadius = 9 + t * 9;
    const coreRadius = 7 + t * 7;
    return [
      // Blue glow ring + bright core ring around the selected substation,
      // mirroring the selected-circuit highlight styling.
      new ScatterplotLayer({
        id: 'regulator-substation-highlight-glow',
        data: [{ position: [lon, lat] }],
        pickable: false,
        stroked: true,
        filled: false,
        radiusUnits: 'pixels',
        radiusMinPixels: 8,
        radiusMaxPixels: 22,
        lineWidthUnits: 'pixels',
        lineWidthMinPixels: 4 + t * 2,
        parameters: { depthTest: false },
        getPosition: (d) => d.position,
        getRadius: glowRadius,
        getLineColor: [96, 165, 250, 140],
        updateTriggers: { getPosition: selectionTrigger, getRadius: [glowRadius] },
      }),
      new ScatterplotLayer({
        id: 'regulator-substation-highlight-core',
        data: [{ position: [lon, lat] }],
        pickable: false,
        stroked: true,
        filled: false,
        radiusUnits: 'pixels',
        radiusMinPixels: 6,
        radiusMaxPixels: 18,
        lineWidthUnits: 'pixels',
        lineWidthMinPixels: 2 + t,
        parameters: { depthTest: false },
        getPosition: (d) => d.position,
        getRadius: coreRadius,
        getLineColor: [37, 99, 235, 255],
        updateTriggers: { getPosition: selectionTrigger, getRadius: [coreRadius] },
      }),
    ];
  }, [substationsVisible, selectedSubstation, currentZoom]);

  const warehouseLayers = useMemo(() => {
    if (
      !warehousesVisible ||
      !Array.isArray(warehouses) ||
      warehouses.length === 0
    )
      return [];
    const processed = warehouses.flatMap((warehouse, index) => {
      const polygons = extractPolygonCoordinateSets(warehouse.geometry);
      return polygons.map((coordinates, polygonIndex) => ({
        ...warehouse,
        id:
          polygons.length > 1
            ? `${warehouse.id ?? index}_${polygonIndex}`
            : (warehouse.id ?? index),
        coordinates,
      }));
    });
    if (processed.length === 0) return [];
    return [
      new PolygonLayer({
        id: 'regulator-warehouses-layer',
        data: processed,
        pickable: true,
        filled: true,
        stroked: true,
        getPolygon: (d) => d.coordinates,
        getFillColor: [4, 120, 87, 130],
        getLineColor: [4, 120, 87, 255],
        getLineWidth: 2,
        opacity: 0.5,
        _normalize: true,
        onClick: (info) => {
          if (info?.object && onWarehouseClick) {
            onWarehouseClick(info.object, info.coordinate);
          }
        },
      }),
    ];
  }, [warehousesVisible, warehouses, onWarehouseClick]);

  const truckStopLayers = useMemo(() => {
    if (!Array.isArray(truckStops) || truckStops.length === 0) return [];
    const intermodal = truckStops
      .filter((s) => s.type === 'intermodal_terminal')
      .map((s) => {
        const lat = parseFloat(s.latitude);
        const lng = parseFloat(s.longitude);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
        return { ...s, position: [lng, lat], color: [175, 110, 200] };
      })
      .filter(Boolean);
    const service = truckStops
      .filter((s) => s.type === 'service_center')
      .map((s) => {
        const lat = parseFloat(s.latitude);
        const lng = parseFloat(s.longitude);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
        return { ...s, position: [lng, lat], color: [90, 170, 188] };
      })
      .filter(Boolean);
    const layers = [];
    if (intermodalTerminalsVisible && intermodal.length > 0) {
      layers.push(
        new ScatterplotLayer({
          id: 'regulator-intermodal-layer',
          data: intermodal,
          pickable: true,
          opacity: 0.8,
          stroked: true,
          filled: true,
          radiusMinPixels: 8,
          radiusMaxPixels: 12,
          getPosition: (d) => d.position,
          getRadius: 12,
          getFillColor: (d) => d.color,
          getLineColor: [255, 255, 255],
          onClick: (info) => {
            if (info?.object && onTruckStopClick) {
              onTruckStopClick(info.object, info.coordinate);
            }
          },
        })
      );
    }
    if (serviceCentersVisible && service.length > 0) {
      layers.push(
        new ScatterplotLayer({
          id: 'regulator-service-centers-layer',
          data: service,
          pickable: true,
          opacity: 0.8,
          stroked: true,
          filled: true,
          radiusMinPixels: 8,
          radiusMaxPixels: 12,
          getPosition: (d) => d.position,
          getRadius: 12,
          getFillColor: (d) => d.color,
          getLineColor: [255, 255, 255],
          onClick: (info) => {
            if (info?.object && onTruckStopClick) {
              onTruckStopClick(info.object, info.coordinate);
            }
          },
        })
      );
    }
    return layers;
  }, [
    intermodalTerminalsVisible,
    serviceCentersVisible,
    truckStops,
    onTruckStopClick,
  ]);

  const trafficLayers = useMemo(() => {
    if (
      !trafficVolumeVisible ||
      !Array.isArray(trafficData) ||
      trafficData.length === 0
    )
      return [];
    const processed = trafficData
      .map((t) => {
        let lng, lat;
        if (t.location?.type === 'Point' && Array.isArray(t.location?.coordinates)) {
          [lng, lat] = t.location.coordinates;
        } else if (typeof t.location === 'string') {
          const m = t.location.match(/POINT\s*\(\s*([^)]+)\s*\)/);
          if (!m) return null;
          [lng, lat] = m[1].split(/\s+/).map(Number);
        } else return null;
        if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
        const hex = getTrafficPointColor(t);
        const rgb = hexToRgb(hex);
        return { ...t, position: [lng, lat], color: rgb };
      })
      .filter(Boolean);
    if (processed.length === 0) return [];
    return [
      new ScatterplotLayer({
        id: 'regulator-traffic-layer',
        data: processed,
        pickable: true,
        opacity: 0.8,
        stroked: true,
        filled: true,
        radiusMinPixels: 4,
        radiusMaxPixels: 12,
        getPosition: (d) => d.position,
        getFillColor: (d) => d.color,
        getLineColor: [255, 255, 255],
      }),
    ];
  }, [trafficVolumeVisible, trafficData]);

  const utilitiesLayers = useMemo(() => {
    if (
      !utilitiesVisible ||
      !Array.isArray(utilitiesData) ||
      utilitiesData.length === 0
    )
      return [];
    const processed = (utilitiesData || []).flatMap((utility) => {
      const { geometry, ...properties } = utility;
      if (!geometry?.coordinates) return [];
      let polygons = [];
      if (geometry.type === 'Polygon') {
        polygons = [geometry.coordinates[0].map(([lng, lat]) => [lng, lat])];
      } else if (geometry.type === 'MultiPolygon') {
        polygons = geometry.coordinates.map((poly) =>
          poly[0].map(([lng, lat]) => [lng, lat])
        );
      }
      const utilityColor = hexToRgb(
        UTILITY_COLORS[(utility.id ?? 0) % UTILITY_COLORS.length]
      );
      return polygons.map((polygon) => ({
        ...properties,
        polygon,
        color: utilityColor,
      }));
    });
    if (processed.length === 0) return [];
    return [
      new PolygonLayer({
        id: 'regulator-utilities-layer',
        data: processed,
        pickable: true,
        opacity: 0.3,
        stroked: true,
        filled: true,
        getPolygon: (d) => d.polygon,
        getFillColor: (d) => d.color,
        getLineWidth: 2,
        lineWidthMinPixels: 1,
      }),
    ];
  }, [utilitiesVisible, utilitiesData]);

  const sraLayers = useMemo(() => {
    if (!sraVisible || !Array.isArray(sraData) || sraData.length === 0) return [];
    const processed = [];
    sraData.forEach((feature, index) => {
      const geom = feature?.geometry;
      if (!geom?.coordinates) return;
      if (geom.type === 'Polygon') {
        processed.push({ ...feature, coordinates: geom.coordinates });
      } else if (geom.type === 'MultiPolygon') {
        geom.coordinates.forEach((polyCoords, polyIndex) => {
          processed.push({
            ...feature,
            id: `${feature.id ?? index}_${polyIndex}`,
            coordinates: polyCoords,
          });
        });
      }
    });
    if (processed.length === 0) return [];
    const textData = processed
      .map((item) => {
        const position = getPolygonCenter(item.coordinates);
        if (!position) return null;
        return {
          ...item,
          position,
          label: String(item?.properties?.monitoring ?? item?.monitoring ?? ''),
        };
      })
      .filter((item) => item && item.label);
    return [
      new PolygonLayer({
        id: 'regulator-sra-layer',
        data: processed,
        pickable: true,
        filled: true,
        stroked: true,
        getPolygon: (d) => d.coordinates,
        getFillColor: [14, 165, 233, 55],
        getLineColor: [8, 145, 178, 255],
        getLineWidth: 3,
        lineWidthMinPixels: 2,
        opacity: 0.5,
        onClick: (info) => {
          if (info?.object && onSraClick) onSraClick(info.object, info.coordinate);
        },
      }),
      new TextLayer({
        id: 'regulator-sra-text-layer',
        data: textData,
        pickable: true,
        getPosition: (d) => d.position,
        getText: (d) => d.label,
        getSize: 12,
        sizeUnits: 'pixels',
        getColor: [0, 67, 99, 255],
        getTextAnchor: 'middle',
        getAlignmentBaseline: 'center',
        getBackgroundColor: [255, 255, 255, 210],
        background: true,
        characterSet: 'auto',
        onClick: (info) => {
          if (info?.object && onSraClick) onSraClick(info.object, info.coordinate);
        },
      }),
    ];
  }, [sraVisible, sraData, onSraClick]);

  const disadvantagedLayers = useMemo(() => {
    if (
      !overburdenedVisible ||
      !Array.isArray(disadvantagedCommunities) ||
      disadvantagedCommunities.length === 0
    )
      return [];
    const processed = [];
    disadvantagedCommunities.forEach((community, index) => {
      const geom = community?.geometry;
      if (!geom?.coordinates) return;
      if (geom.type === 'Polygon') {
        processed.push({ ...community, coordinates: geom.coordinates });
      } else if (geom.type === 'MultiPolygon') {
        geom.coordinates.forEach((polyCoords, polyIndex) => {
          processed.push({
            ...community,
            id: `${community.id ?? index}_${polyIndex}`,
            coordinates: polyCoords,
          });
        });
      }
    });
    if (processed.length === 0) return [];
    return [
      new PolygonLayer({
        id: 'regulator-disadvantaged-layer',
        data: processed,
        pickable: true,
        filled: true,
        stroked: true,
        getPolygon: (d) => d.coordinates,
        getFillColor: [220, 38, 38, 80],
        getLineColor: [220, 38, 38, 255],
        getLineWidth: 2,
        opacity: 0.5,
      }),
    ];
  }, [overburdenedVisible, disadvantagedCommunities]);

  const calEnviroScreenLayers = useMemo(() => {
    if (!calEnviroScreenVisible || !Array.isArray(calEnviroScreen) || calEnviroScreen.length === 0) {
      return [];
    }
    const getPercentile = (tract) => {
      const raw =
        tract?.properties?.ciscorep ??
        tract?.properties?.CIscoreP ??
        tract?.ciscorep ??
        tract?.CIscoreP;
      const parsed = Number(raw);
      if (!Number.isFinite(parsed)) return null;
      return Math.max(0, Math.min(100, parsed));
    };
    const getColorByPercentile = (percentile, alpha = 120) => {
      const p = Number.isFinite(percentile) ? percentile / 100 : 0;
      const red = Math.round(255 * p);
      const green = Math.round(255 * (1 - p));
      return [red, green, 40, alpha];
    };

    const processedTracts = [];
    calEnviroScreen.forEach((tract, index) => {
      const geom = tract?.geometry;
      if (!geom?.coordinates) return;
      if (geom.type === 'Polygon') {
        processedTracts.push({
          ...tract,
          percentile: getPercentile(tract),
          coordinates: geom.coordinates,
        });
      } else if (geom.type === 'MultiPolygon') {
        geom.coordinates.forEach((polygonCoords, polyIndex) => {
          processedTracts.push({
            ...tract,
            id: `${tract.id ?? index}_${polyIndex}`,
            percentile: getPercentile(tract),
            coordinates: polygonCoords,
          });
        });
      }
    });
    if (processedTracts.length === 0) return [];
    return [
      new PolygonLayer({
        id: 'regulator-calenviroscreen-40-layer',
        data: processedTracts,
        pickable: true,
        filled: true,
        stroked: true,
        getPolygon: (d) => d.coordinates,
        getFillColor: (d) => getColorByPercentile(d.percentile, 145),
        getLineColor: (d) => getColorByPercentile(d.percentile, 255),
        getLineWidth: 2.5,
        lineWidthMinPixels: 2,
        lineWidthMaxPixels: 4,
        opacity: 0.9,
      }),
    ];
  }, [calEnviroScreenVisible, calEnviroScreen]);

  const countiesLayers = useMemo(() => {
    if (!countiesVisible || !Array.isArray(countiesData) || countiesData.length === 0)
      return [];
    const processed = [];
    countiesData.forEach((county, index) => {
      const geom = county?.geometry;
      if (!geom?.coordinates) return;
      if (geom.type === 'Polygon') {
        processed.push({ ...county, coordinates: geom.coordinates });
      } else if (geom.type === 'MultiPolygon') {
        geom.coordinates.forEach((polyCoords, polyIndex) => {
          processed.push({
            ...county,
            id: `${county.GEOID || county.id || index}_${polyIndex}`,
            coordinates: polyCoords,
          });
        });
      }
    });
    if (processed.length === 0) return [];
    return [
      new PolygonLayer({
        id: 'regulator-counties-layer',
        data: processed,
        pickable: true,
        filled: true,
        stroked: true,
        getPolygon: (d) => d.coordinates,
        // Utility-like translucent area, but with stronger contrast and clear borders.
        getFillColor: (d) => hexToRgb(getCountyColorHex(d), 80),
        getLineColor: [255, 255, 255, 240],
        getLineWidth: 2.5,
        lineWidthMinPixels: 2,
        opacity: 0.4,
      }),
    ];
  }, [countiesVisible, countiesData]);

  const allLayers = useMemo(
    () =>
      moveLayerGroupToTop(
        [
          { id: 'parcels', layers: parcelLayers },
          { id: 'powerGridCapacity', layers: circuitLayers },
          { id: 'truckHotspots', layers: heatmapLayers },
          { id: 'powerGridHeatmap', layers: contourLayers },
          { id: 'powerGridCapacity', layers: selectedCircuitLayers },
          { id: 'substations', layers: selectedSubstationLayers },
          { id: 'substations', layers: substationLayers },
          { id: 'existingChargers', layers: mhdLayers },
          { id: 'warehouses', layers: warehouseLayers },
          {
            id: 'intermodalTerminals',
            layers: truckStopLayers.filter((layer) => layer.id === 'regulator-intermodal-layer'),
          },
          {
            id: 'serviceCenters',
            layers: truckStopLayers.filter((layer) => layer.id === 'regulator-service-centers-layer'),
          },
          { id: 'trafficVolume', layers: trafficLayers },
          { id: 'utilities', layers: utilitiesLayers },
          { id: 'sra', layers: sraLayers },
          { id: 'counties', layers: countiesLayers },
          { id: 'overburdened', layers: disadvantagedLayers },
          { id: 'calEnviroScreen', layers: calEnviroScreenLayers },
        ],
        interactiveTopLayerId
      ),
    [
      parcelLayers,
      circuitLayers,
      heatmapLayers,
      contourLayers,
      selectedCircuitLayers,
      selectedSubstationLayers,
      substationLayers,
      mhdLayers,
      warehouseLayers,
      truckStopLayers,
      trafficLayers,
      utilitiesLayers,
      sraLayers,
      countiesLayers,
      disadvantagedLayers,
      calEnviroScreenLayers,
      interactiveTopLayerId,
    ]
  );

  useEffect(() => {
    if (!map || overlayRef.current) return;
    try {
      overlayRef.current = new GoogleMapsOverlay({
        glOptions: {
          antialias: false,
          alpha: true,
        },
        interleaved: true,
        pickingRadius: 10,
      });
      overlayRef.current.setMap(map);
    } catch (e) {
      logger.error('RegulatorChargerLayersOverlay init error', e);
    }
    return () => {
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
        overlayRef.current = null;
      }
    };
  }, [map]);

  useEffect(() => {
    if (!overlayRef.current) return;
    overlayRef.current.setProps({ layers: allLayers, getTooltip });
  }, [allLayers, getTooltip]);

  return null;
};
